import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { getSupabaseClient } from '@/lib/supabase';
import logger from '@/lib/logger';

export default function AuthCallbackPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { syncUser } = useAuth();

  // Prevent multiple runs of the callback handler
  const hasHandledCallback = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Guard against multiple runs
      if (hasHandledCallback.current) {
        logger.info('Auth callback already handled, skipping');
        return;
      }
      hasHandledCallback.current = true;

      // Read accountType from localStorage (set before OAuth redirect)
      let accountTypeFromState: 'player' | 'coach' | null = null;
      try {
        const pending = localStorage.getItem('pendingAccountType') as 'player' | 'coach' | null;
        if (pending) {
          accountTypeFromState = pending;
          logger.info('auth-callback: got accountType from localStorage', { accountType: accountTypeFromState });
        }
      } catch (err) {
        logger.warn('auth-callback: failed to read pendingAccountType', err);
      }

      try {
        const supabase = await getSupabaseClient();
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          logger.error('Auth callback error:', error as any);
          toast({
            variant: 'destructive',
            title: 'Authentication error',
            description: error.message || 'There was an error with authentication.',
          });
          setLocation('/login');
          return;
        }

        if (data.session) {
          // CRITICAL: Use syncUser to ensure user is created in our database
          // This handles the case where user confirms email and needs to be created
          logger.info('Auth callback: syncing user to database...', { accountTypeFromState });
          const dbUser = await syncUser(accountTypeFromState);
          
          if (!dbUser) {
            logger.error('Auth callback: Failed to sync user to database');
            toast({
              variant: 'destructive',
              title: 'Account setup failed',
              description: 'There was an error setting up your account. Please try again.',
            });
            setLocation('/login');
            return;
          }
          
          logger.info('Auth callback: User synced successfully', { userId: dbUser.id, credits: dbUser.credits });
          
          // Check if this is email confirmation or password reset
          const params = new URLSearchParams(window.location.hash.substring(1));
          const type = params.get('type');
          
          if (type === 'email_confirmation') {
            toast({
              title: 'Email confirmed!',
              description: `Your email has been verified. You have ${dbUser.credits} free credits to get started!`,
            });
          } else if (type === 'recovery') {
            toast({
              title: 'Password reset',
              description: 'You can now set a new password.',
            });
            setLocation('/reset-password');
            return;
          } else {
            toast({
              title: 'Welcome to Bounce Bunny!',
              description: 'You have been successfully signed in.',
            });
          }

          // Redirect coaches to create-team, players to home
          if (dbUser.accountType === 'coach') {
            setLocation('/create-team');
          } else {
            setLocation('/');
          }
        } else {
          setLocation('/login');
        }
      } catch (error) {
        logger.error('Callback handling error:', error as any);
        toast({
          variant: 'destructive',
          title: 'Authentication failed',
          description: 'There was an error processing the authentication.',
        });
        setLocation('/login');
      }
    };

    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-apple-blue via-apple-purple to-apple-pink flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4" />
        <p className="text-white/80">Completing sign in...</p>
      </div>
    </div>
  );
}